import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";

// Onzichtbare botbescherming: uitdaging ophalen, sha256-puzzel stil oplossen, Turnstile laden indien ingesteld.
async function sha256Hex(text) {
  const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(text));
  return Array.from(new Uint8Array(buf), (b) => b.toString(16).padStart(2, "0")).join("");
}

async function solve(challenge, prefix) {
  for (let n = 0; n < 5_000_000; n++) {
    if ((await sha256Hex(`${challenge}:${n}`)).startsWith(prefix)) return String(n);
    if (n % 500 === 0) await new Promise((r) => setTimeout(r, 0)); // UI niet blokkeren
  }
  return "";
}

let turnstileScript;
function loadTurnstile() {
  if (window.turnstile) return Promise.resolve();
  if (!turnstileScript) {
    turnstileScript = new Promise((resolve) => {
      const s = document.createElement("script");
      s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      s.async = true; s.onload = resolve; s.onerror = resolve;
      document.head.appendChild(s);
    });
  }
  return turnstileScript;
}

export function useBotGuard() {
  const [state, setState] = useState({ challenge: null, nonce: null, turnstile: null, siteKey: null });
  const widgetRef = useRef(null);
  const widgetId = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/challenge");
      setState((s) => ({ ...s, challenge: data.challenge, nonce: null, siteKey: data.turnstile_site_key }));
      const nonce = await solve(data.challenge, data.prefix);
      setState((s) => (s.challenge === data.challenge ? { ...s, nonce } : s));
    } catch { /* zonder uitdaging valt de server terug op de IP-limiet */ }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    if (!state.siteKey || !widgetRef.current || widgetId.current !== null) return;
    loadTurnstile().then(() => {
      if (!window.turnstile || !widgetRef.current) return;
      widgetId.current = window.turnstile.render(widgetRef.current, {
        sitekey: state.siteKey, appearance: "interaction-only", theme: "auto",
        callback: (token) => setState((s) => ({ ...s, turnstile: token })),
        "expired-callback": () => setState((s) => ({ ...s, turnstile: null })),
      });
    });
  }, [state.siteKey]);

  const resetTurnstile = () => { if (window.turnstile && widgetId.current !== null) window.turnstile.reset(widgetId.current); };
  const extras = () => ({ challenge: state.challenge, nonce: state.nonce, turnstile: state.turnstile });
  return { extras, refresh, resetTurnstile, widgetRef, ready: !!state.nonce && (!state.siteKey || !!state.turnstile) };
}
